/**
 * @file catalog-universal.ts
 * @description Универсальные заготовки секций (phase19.16 Р2; решение
 * пользователя 2026-10-10; SB25, SB27): «О нас» с цитатой, этапы, команда,
 * сертификаты, доставка и оплата, «Где нас найти», «Остались вопросы?»,
 * публикация крупно. Нейтральны: без товаров, картинок и названий магазина;
 * контакты — только из «Настроек сайта» (SB25), поэтому мессенджер и телефон
 * показываются элементом «Контакты», а не кнопками с вписанным адресом.
 * Место в библиотеке задаёт группа — реестр сортируется по группам (index.ts).
 */

import { defaultSectionLook } from "../defaults.js";
import { catalogButton, el, eyebrow, header, heading, section, text } from "./build.js";
import { GENERAL_PRESET_CONTEXTS, type SectionPreset } from "./types.js";

const ABOUT_TEXT = "Короткий текст о том, чем вы занимаетесь.";

function preset(
  p: Omit<SectionPreset, "previewKey" | "backgroundPhoto" | "requires" | "contexts"> & Partial<SectionPreset>,
): SectionPreset {
  return { previewKey: p.key, backgroundPhoto: false, requires: null, contexts: GENERAL_PRESET_CONTEXTS, ...p };
}

/** Плитки-заглушки: место под фото, заголовок и строка текста, без ссылки. */
const tileItems = (titles: readonly string[], line: string) =>
  titles.map((title) => ({ mediaId: null, title, text: line, target: null }));

const SERVICE_COLUMNS = [
  { eyebrow: "Доставка", title: "Как доставляем", line: "Способы и сроки доставки — в двух-трёх предложениях." },
  { eyebrow: "Оплата", title: "Как оплатить", line: "Перечислите способы оплаты, которые принимаете." },
  { eyebrow: "Возврат", title: "Если не подошло", line: "Коротко опишите условия обмена и возврата." },
] as const;

export const SECTION_PRESETS_UNIVERSAL: readonly SectionPreset[] = [
  preset({
    key: "about-photo-quote",
    group: "text-photo",
    title: "О нас с фото и цитатой",
    hint: "История мастерской или бренда",
    build: () =>
      section({
        columns: [
          [
            el("image", {
              mode: "fit",
              // «По колонке» — прежнее «Заполнить колонку».
              height: { kind: "column" },
              overlays: [{ text: "Короткая цитата о вашем деле", preset: "script", x: 0, y: 100, hideOnPhone: false }],
            }),
          ],
          [
            eyebrow("О нас"),
            heading(2, "Заголовок о вашей истории"),
            text(ABOUT_TEXT),
            catalogButton("Перейти в каталог", "outline"),
          ],
        ],
      }),
  }),
  preset({
    key: "steps-3",
    group: "tiles",
    title: "Этапы работы",
    hint: "Как вы работаете: от заказа до получения",
    build: () =>
      section({
        header: header("Как мы работаем"),
        columns: [
          [el("tiles", { cols: 3, look: "below", items: tileItems(["1", "2", "3"], "Опишите этот шаг коротко.") })],
        ],
      }),
  }),
  preset({
    key: "team-cards",
    group: "tiles",
    title: "Команда или мастера",
    hint: "Люди, которые делают вашу работу",
    build: () =>
      section({
        header: header("Команда"),
        columns: [[el("tiles", { cols: 4, look: "below", items: tileItems(["Имя", "Имя", "Имя", "Имя"], "Роль") })]],
      }),
  }),
  preset({
    key: "certificates-gallery",
    group: "text-photo",
    title: "Сертификаты и документы",
    hint: "Сертификаты, декларации, награды",
    build: () =>
      section({
        header: header("Сертификаты и документы"),
        columns: [[el("gallery", { mediaIds: [], perRow: 4, phoneLayout: "scroll" })]],
      }),
  }),
  preset({
    key: "delivery-payment-3col",
    group: "service",
    title: "Доставка и оплата в три колонки",
    hint: "Доставка, оплата и возврат коротко",
    build: () =>
      section({
        valign: "top",
        // Ссылки ведут в каталог как заглушка: страницу условий владелец выбирает сам.
        columns: SERVICE_COLUMNS.map((c) => [
          eyebrow(c.eyebrow),
          heading(3, c.title),
          text(c.line),
          catalogButton("Подробнее", "link"),
        ]),
      }),
  }),
  preset({
    key: "where-to-find",
    group: "service",
    title: "Где нас найти",
    hint: "Адрес, телефон и мессенджер",
    build: () =>
      section({
        valign: "top",
        columns: [
          [heading(2, "Где нас найти"), text("Как добраться и когда мы на месте — коротко.")],
          [el("contacts", { kinds: ["address", "phone", "telegram"] })],
        ],
      }),
  }),
  preset({
    key: "questions-left",
    group: "cta",
    title: "Остались вопросы?",
    hint: "Связаться в мессенджере или по телефону",
    build: () =>
      section({
        look: { ...defaultSectionLook(), bg: { kind: "role", role: "panelBg" } },
        columns: [
          [
            heading(2, "Остались вопросы?"),
            text("Напишите или позвоните — подскажем и поможем выбрать."),
            // SB25: адреса — из «Настроек сайта», вписать в блок нельзя.
            el("contacts", { kinds: ["telegram", "phone"] }),
          ],
        ],
      }),
  }),
  preset({
    key: "blog-feed-header",
    group: "blog",
    title: "Шапка ленты блога",
    hint: "Своя шапка над статьями вместо встроенной",
    build: () =>
      section({
        look: {
          ...defaultSectionLook(),
          space: { ...defaultSectionLook().space, outerTop: 0, outerBottom: 32 },
        },
        columns: [
          [
            el("eyebrow", { text: "Надпись над заголовком", preset: "caps", align: "center" }),
            el("heading", { level: 1, text: "Блог", align: "center" }),
            el("text", { text: "Пара предложений о том, о чём вы пишете.", align: "center" }),
          ],
        ],
      }),
  }),
  preset({
    key: "article-featured",
    group: "blog",
    title: "Публикация крупно",
    hint: "Свежая или выбранная статья блога",
    build: () =>
      section({
        header: header("Из блога"),
        // Без выбранной статьи — свежая; выбрать вручную владелец может после вставки.
        columns: [[el("articles", { source: "latest", limit: 1 })]],
      }),
  }),
];
